import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Calendar as CalendarIcon, 
  Clock, 
  Users, 
  User, 
  Mail, 
  Phone, 
  MapPin, 
  CreditCard, 
  Banknote,
  Check,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  Loader2,
  Truck
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Calendar } from './ui/calendar';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { formatPrice, getAvailableTimes } from '../lib/utils';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BookingModal = ({ isOpen, onClose, selectedTour, tours }) => {
  const { language, t } = useLanguage();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [bookingSuccess, setBookingSuccess] = useState(false);
  const [bookingId, setBookingId] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    tourId: '',
    date: null,
    time: '',
    adults: 1,
    children: 0,
    childSeats: 0,
    name: '',
    email: '',
    phone: '',
    pickupType: 'hotel',
    hotel: '',
    pickupPoint: '',
    specialRequests: '',
    paymentMethod: 'online',
    durationOption: '3h', // For "Un Día en Madrid"
    discountCode: ''
  });

  // Discount validation state
  const [discountValidation, setDiscountValidation] = useState(null);
  const [validatingDiscount, setValidatingDiscount] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  // Tuk-tuk availability state
  const [tukTuks, setTukTuks] = useState([]);
  const [loadingTukTuks, setLoadingTukTuks] = useState(false);
  const [selectedTukTuk, setSelectedTukTuk] = useState(null);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setBookingSuccess(false);
      setBookingId(null);
      setAcceptedTerms(false);
      setTukTuks([]);
      setSelectedTukTuk(null);
      if (selectedTour) {
        setFormData(prev => ({ ...prev, tourId: selectedTour.id }));
      }
    }
  }, [isOpen, selectedTour]);

  // Fetch tuk-tuk availability when date, time, or tour changes
  useEffect(() => {
    const fetchTukTuks = async () => {
      if (!formData.date || !formData.time || !formData.tourId) {
        setTukTuks([]);
        setSelectedTukTuk(null);
        return;
      }
      setLoadingTukTuks(true);
      try {
        const dateStr = formData.date.toISOString().split('T')[0];
        const params = new URLSearchParams({
          date: dateStr,
          time: formData.time,
          tour_id: formData.tourId,
        });
        if (formData.tourId === 'un-dia-madrid') {
          params.append('duration_option', formData.durationOption);
        }
        const res = await axios.get(`${API}/availability/tuktuks?${params}`);
        const list = res.data.tuk_tuks || [];
        setTukTuks(list);
        // Auto-select if only one available
        const available = list.filter(t => t.available);
        if (available.length === 1) {
          setSelectedTukTuk(available[0].id);
        } else if (selectedTukTuk) {
          // Check if previously selected is still available
          const stillAvail = list.find(t => t.id === selectedTukTuk && t.available);
          if (!stillAvail) setSelectedTukTuk(null);
        }
      } catch (err) {
        console.error('Error fetching tuk-tuk availability:', err);
        setTukTuks([]);
      } finally {
        setLoadingTukTuks(false);
      }
    };
    fetchTukTuks();
  }, [formData.date, formData.time, formData.tourId, formData.durationOption]);

  const selectedTourData = tours?.find(t => t.id === formData.tourId) || selectedTour;

  const calculateTotal = () => {
    if (!selectedTourData) return 0;
    let basePrice;
    if (formData.tourId === 'un-dia-madrid' && formData.durationOption === '6h') {
      basePrice = selectedTourData.price_6h || 400;
    } else {
      basePrice = selectedTourData.price || 0;
    }
    
    // Apply discount if validated
    if (discountValidation?.valid) {
      if (discountValidation.discount_type === 'percentage') {
        return basePrice * (1 - discountValidation.discount_value / 100);
      } else {
        return Math.max(0, basePrice - discountValidation.discount_value);
      }
    }
    return basePrice;
  };

  const getOriginalPrice = () => {
    if (!selectedTourData) return 0;
    if (formData.tourId === 'un-dia-madrid' && formData.durationOption === '6h') {
      return selectedTourData.price_6h || 400;
    }
    return selectedTourData.price || 0;
  };

  const validateDiscount = async () => {
    if (!formData.discountCode) return;
    
    setValidatingDiscount(true);
    try {
      const response = await axios.post(`${API}/discounts/validate`, {
        code: formData.discountCode,
        adults: formData.adults,
        tour_id: formData.tourId
      });
      setDiscountValidation(response.data);
      toast.success(response.data.message);
    } catch (error) {
      setDiscountValidation(null);
      toast.error(error.response?.data?.detail || 'Código inválido');
    } finally {
      setValidatingDiscount(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const canProceed = () => {
    switch (step) {
      case 1: {
        const baseReady = formData.tourId && formData.date && formData.time;
        if (!baseReady) return false;
        // If tuk-tuks loaded, require selection
        const availableTukTuks = tukTuks.filter(t => t.available);
        if (tukTuks.length > 0 && availableTukTuks.length > 0) {
          return !!selectedTukTuk;
        }
        // If no tuk-tuks exist yet (not seeded) or still loading, allow proceed
        return !loadingTukTuks;
      }
      case 2:
        return formData.adults >= 1 && (formData.adults + formData.children) <= 4;
      case 3:
        return formData.name && formData.email && formData.phone && 
               (formData.pickupType === 'hotel' ? formData.hotel : formData.pickupPoint);
      case 4:
        return formData.paymentMethod && acceptedTerms;
      default:
        return false;
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      // Create booking
      const bookingData = {
        tour_id: formData.tourId,
        date: formData.date?.toISOString().split('T')[0],
        time: formData.time,
        tuk_tuk_id: selectedTukTuk || null,
        adults: formData.adults,
        children: formData.children,
        child_seats_needed: formData.childSeats,
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        hotel: formData.pickupType === 'hotel' ? formData.hotel : null,
        pickup_point: formData.pickupType === 'point' ? formData.pickupPoint : null,
        special_requests: formData.specialRequests,
        payment_method: formData.paymentMethod,
        language: language,
        duration_option: formData.tourId === 'un-dia-madrid' ? formData.durationOption : null,
        discount_code: discountValidation?.valid ? formData.discountCode : null
      };

      const response = await axios.post(`${API}/bookings`, bookingData);
      const booking = response.data;
      setBookingId(booking.id);

      if (formData.paymentMethod === 'online') {
        // Create Stripe checkout session
        const checkoutResponse = await axios.post(`${API}/checkout/create-session`, {
          booking_id: booking.id,
          origin_url: window.location.origin
        });

        // Redirect to Stripe
        window.location.href = checkoutResponse.data.url;
      } else {
        // Cash payment - show success
        setBookingSuccess(true);
        toast.success(t.booking.success);
      }
    } catch (error) {
      console.error('Booking error:', error);
      if (error.response?.status === 409) {
        toast.error(error.response.data.detail || 'Este tuk-tuk ya no está disponible');
        // Refresh availability
        setSelectedTukTuk(null);
      } else {
        toast.error(t.booking.error);
      }
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    { number: 1, label: t.booking.selectTour },
    { number: 2, label: t.booking.guests },
    { number: 3, label: t.booking.contactInfo },
    { number: 4, label: t.booking.payment }
  ];

  const times = getAvailableTimes();
  const pickupPoints = [
    { value: 'opera', label: t.booking.pickupPoints.opera },
    { value: 'neptuno', label: t.booking.pickupPoints.neptuno },
    { value: 'prado', label: t.booking.pickupPoints.prado },
    { value: 'debod', label: t.booking.pickupPoints.debod }
  ];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="booking-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-center">
            {bookingSuccess ? t.booking.success : t.booking.title}
          </DialogTitle>
        </DialogHeader>

        {/* Success State */}
        {bookingSuccess ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center py-8"
          >
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-green-100 flex items-center justify-center">
              <Check className="h-10 w-10 text-green-600" />
            </div>
            <h3 className="text-xl font-semibold mb-2">{t.booking.success}</h3>
            <p className="text-[#57534E] mb-6">{t.booking.successMessage}</p>
            <p className="text-sm text-[#57534E]">
              Booking ID: <span className="font-mono font-semibold">{bookingId}</span>
            </p>
            <Button
              onClick={onClose}
              className="mt-6 bg-[#C2410C] hover:bg-[#9A3412] rounded-full"
              data-testid="booking-close-btn"
            >
              Cerrar
            </Button>
          </motion.div>
        ) : (
          <>
            {/* Progress Steps */}
            <div className="flex items-center justify-between mb-8 px-4">
              {steps.map((s, index) => (
                <React.Fragment key={s.number}>
                  <div 
                    className={`flex flex-col items-center ${
                      step >= s.number ? 'text-[#C2410C]' : 'text-[#E7E5E4]'
                    }`}
                  >
                    <div 
                      className={`w-10 h-10 rounded-full flex items-center justify-center font-bold transition-colors ${
                        step >= s.number 
                          ? 'bg-[#C2410C] text-white' 
                          : 'bg-[#E7E5E4] text-[#57534E]'
                      }`}
                    >
                      {step > s.number ? <Check className="h-5 w-5" /> : s.number}
                    </div>
                    <span className="text-xs mt-2 hidden sm:block">{s.label}</span>
                  </div>
                  {index < steps.length - 1 && (
                    <div 
                      className={`flex-1 h-1 mx-2 rounded ${
                        step > s.number ? 'bg-[#C2410C]' : 'bg-[#E7E5E4]'
                      }`} 
                    />
                  )}
                </React.Fragment>
              ))}
            </div>

            {/* Step Content */}
            <AnimatePresence mode="wait">
              <motion.div
                key={step}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.2 }}
                className="min-h-[300px]"
              >
                {/* Step 1: Select Tour & Date */}
                {step === 1 && (
                  <div className="space-y-6">
                    {/* Tour Selection */}
                    <div>
                      <Label className="text-base font-semibold mb-3 block">
                        {t.booking.selectTour}
                      </Label>
                      <Select
                        value={formData.tourId}
                        onValueChange={(value) => handleInputChange('tourId', value)}
                      >
                        <SelectTrigger data-testid="tour-select">
                          <SelectValue placeholder="Selecciona un tour" />
                        </SelectTrigger>
                        <SelectContent>
                          {tours?.map((tour) => (
                            <SelectItem key={tour.id} value={tour.id}>
                              {tour.name[language] || tour.name.es} - {formatPrice(tour.price)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Duration option for "Un Día en Madrid" */}
                    {formData.tourId === 'un-dia-madrid' && (
                      <div>
                        <Label className="text-base font-semibold mb-3 block">
                          Duración
                        </Label>
                        <RadioGroup
                          value={formData.durationOption}
                          onValueChange={(value) => handleInputChange('durationOption', value)}
                          className="flex gap-4"
                        >
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="3h" id="duration-3h" />
                            <Label htmlFor="duration-3h">
                              {t.booking.option3h} - {formatPrice(209)}
                            </Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="6h" id="duration-6h" />
                            <Label htmlFor="duration-6h">
                              {t.booking.option6h} - {formatPrice(400)}
                            </Label>
                          </div>
                        </RadioGroup>
                      </div>
                    )}

                    {/* Date Selection */}
                    <div>
                      <Label className="text-base font-semibold mb-3 block">
                        <CalendarIcon className="h-4 w-4 inline mr-2" />
                        {t.booking.selectDate}
                      </Label>
                      <div className="flex justify-center">
                        <Calendar
                          mode="single"
                          selected={formData.date}
                          onSelect={(date) => handleInputChange('date', date)}
                          disabled={(date) => date < new Date()}
                          className="rounded-md border"
                          data-testid="booking-calendar"
                        />
                      </div>
                    </div>

                    {/* Time Selection */}
                    <div>
                      <Label className="text-base font-semibold mb-3 block">
                        <Clock className="h-4 w-4 inline mr-2" />
                        {t.booking.selectTime}
                      </Label>
                      <Select
                        value={formData.time}
                        onValueChange={(value) => handleInputChange('time', value)}
                      >
                        <SelectTrigger data-testid="time-select">
                          <SelectValue placeholder="Selecciona hora" />
                        </SelectTrigger>
                        <SelectContent>
                          {times.map((time) => (
                            <SelectItem key={time} value={time}>
                              {time}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Tuk-Tuk Selection */}
                    {formData.date && formData.time && formData.tourId && (
                      <div data-testid="tuktuk-selection-section">
                        <Label className="text-base font-semibold mb-3 block">
                          <Truck className="h-4 w-4 inline mr-2" />
                          {language === 'en' ? 'Select Tuk-Tuk' : language === 'fr' ? 'Sélectionner Tuk-Tuk' : 'Selecciona Tuk-Tuk'}
                        </Label>
                        {loadingTukTuks ? (
                          <div className="flex items-center gap-2 text-sm text-[#57534E] py-3">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            {language === 'en' ? 'Checking availability...' : language === 'fr' ? 'Vérification...' : 'Comprobando disponibilidad...'}
                          </div>
                        ) : tukTuks.length === 0 ? (
                          <p className="text-sm text-[#78716C] py-2">
                            {language === 'en' ? 'No tuk-tuks configured yet' : language === 'fr' ? 'Aucun tuk-tuk configuré' : 'No hay tuk-tuks configurados aún'}
                          </p>
                        ) : (
                          <div className="space-y-2">
                            {tukTuks.map((tt) => {
                              const isAvailable = tt.available;
                              const isSelected = selectedTukTuk === tt.id;
                              return (
                                <button
                                  key={tt.id}
                                  type="button"
                                  disabled={!isAvailable}
                                  onClick={() => isAvailable && setSelectedTukTuk(tt.id)}
                                  data-testid={`tuktuk-option-${tt.id}`}
                                  className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                                    !isAvailable
                                      ? 'border-[#E7E5E4] bg-[#F5F5F4] opacity-60 cursor-not-allowed'
                                      : isSelected
                                      ? 'border-[#C2410C] bg-[#C2410C]/5 shadow-sm'
                                      : 'border-[#E7E5E4] hover:border-[#C2410C]/50 cursor-pointer'
                                  }`}
                                >
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                                        !isAvailable ? 'bg-[#E7E5E4]' : isSelected ? 'bg-[#C2410C]' : 'bg-[#C2410C]/10'
                                      }`}>
                                        <Truck className={`h-5 w-5 ${
                                          !isAvailable ? 'text-[#A8A29E]' : isSelected ? 'text-white' : 'text-[#C2410C]'
                                        }`} />
                                      </div>
                                      <div>
                                        <p className={`font-medium ${!isAvailable ? 'text-[#A8A29E]' : 'text-[#1C1917]'}`}>
                                          {tt.name}
                                        </p>
                                        <p className={`text-xs ${!isAvailable ? 'text-[#D6D3D1]' : 'text-[#78716C]'}`}>
                                          {tt.capacity} {language === 'en' ? 'seats' : language === 'fr' ? 'places' : 'plazas'}
                                          {tt.driver_name ? ` · ${language === 'en' ? 'Driver' : language === 'fr' ? 'Chauffeur' : 'Conductor'}: ${tt.driver_name}` : ''}
                                        </p>
                                      </div>
                                    </div>
                                    {!isAvailable ? (
                                      <span className="text-xs font-medium text-red-400 bg-red-50 px-2 py-1 rounded-full" data-testid={`tuktuk-unavailable-${tt.id}`}>
                                        {language === 'en' ? 'Already booked' : language === 'fr' ? 'Déjà réservé' : 'Ya reservado'}
                                      </span>
                                    ) : isSelected ? (
                                      <div className="w-6 h-6 rounded-full bg-[#C2410C] flex items-center justify-center">
                                        <Check className="h-4 w-4 text-white" />
                                      </div>
                                    ) : (
                                      <div className="w-6 h-6 rounded-full border-2 border-[#D6D3D1]" />
                                    )}
                                  </div>
                                </button>
                              );
                            })}
                            {tukTuks.every(tt => !tt.available) && (
                              <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
                                <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
                                <p className="text-sm text-red-700">
                                  {language === 'en' 
                                    ? 'No tuk-tuks available for this time. Please choose a different date or time.'
                                    : language === 'fr'
                                    ? 'Aucun tuk-tuk disponible. Veuillez choisir une autre date ou heure.'
                                    : 'No hay tuk-tuks disponibles para este horario. Por favor, elige otra fecha u hora.'}
                                </p>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Step 2: Number of Guests */}
                {step === 2 && (
                  <div className="space-y-6">
                    <div className="bg-[#FEF3C7] border border-[#F59E0B] rounded-lg p-4 flex items-start gap-3">
                      <AlertCircle className="h-5 w-5 text-[#F59E0B] flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-[#92400E]">
                        {t.booking.maxCapacity}
                      </p>
                    </div>

                    {/* Adults */}
                    <div>
                      <Label className="text-base font-semibold mb-3 block">
                        <Users className="h-4 w-4 inline mr-2" />
                        {t.booking.adults}
                      </Label>
                      <div className="flex items-center gap-4">
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          onClick={() => handleInputChange('adults', Math.max(1, formData.adults - 1))}
                          disabled={formData.adults <= 1}
                          data-testid="adults-decrease"
                        >
                          -
                        </Button>
                        <span className="text-2xl font-bold w-12 text-center">{formData.adults}</span>
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          onClick={() => handleInputChange('adults', Math.min(4 - formData.children, formData.adults + 1))}
                          disabled={formData.adults + formData.children >= 4}
                          data-testid="adults-increase"
                        >
                          +
                        </Button>
                      </div>
                    </div>

                    {/* Children */}
                    <div>
                      <Label className="text-base font-semibold mb-3 block">
                        {t.booking.children}
                      </Label>
                      <div className="flex items-center gap-4">
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          onClick={() => handleInputChange('children', Math.max(0, formData.children - 1))}
                          disabled={formData.children <= 0}
                          data-testid="children-decrease"
                        >
                          -
                        </Button>
                        <span className="text-2xl font-bold w-12 text-center">{formData.children}</span>
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          onClick={() => handleInputChange('children', Math.min(4 - formData.adults, formData.children + 1))}
                          disabled={formData.adults + formData.children >= 4}
                          data-testid="children-increase"
                        >
                          +
                        </Button>
                      </div>
                    </div>

                    {/* Child Seats */}
                    {formData.children > 0 && (
                      <div>
                        <Label className="text-base font-semibold mb-3 block">
                          {t.booking.childSeats}
                        </Label>
                        <div className="flex items-center gap-4">
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            onClick={() => handleInputChange('childSeats', Math.max(0, formData.childSeats - 1))}
                            disabled={formData.childSeats <= 0}
                          >
                            -
                          </Button>
                          <span className="text-2xl font-bold w-12 text-center">{formData.childSeats}</span>
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            onClick={() => handleInputChange('childSeats', Math.min(formData.children, formData.childSeats + 1))}
                            disabled={formData.childSeats >= formData.children}
                          >
                            +
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* Group discount info */}
                    <div className="bg-[#FDFBF7] rounded-lg p-4">
                      <p className="text-sm text-[#57534E]">
                        {t.booking.groupDiscount}
                      </p>
                    </div>
                  </div>
                )}

                {/* Step 3: Contact Info */}
                {step === 3 && (
                  <div className="space-y-6">
                    {/* Name */}
                    <div>
                      <Label htmlFor="name" className="text-base font-semibold mb-2 block">
                        <User className="h-4 w-4 inline mr-2" />
                        {t.booking.name}
                      </Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => handleInputChange('name', e.target.value)}
                        placeholder="John Doe"
                        data-testid="booking-name-input"
                      />
                    </div>

                    {/* Email */}
                    <div>
                      <Label htmlFor="email" className="text-base font-semibold mb-2 block">
                        <Mail className="h-4 w-4 inline mr-2" />
                        {t.booking.email}
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        placeholder="john@example.com"
                        data-testid="booking-email-input"
                      />
                    </div>

                    {/* Phone */}
                    <div>
                      <Label htmlFor="phone" className="text-base font-semibold mb-2 block">
                        <Phone className="h-4 w-4 inline mr-2" />
                        {t.booking.phone}
                      </Label>
                      <Input
                        id="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => handleInputChange('phone', e.target.value)}
                        placeholder="+34 612 345 678"
                        data-testid="booking-phone-input"
                      />
                    </div>

                    {/* Pickup Type */}
                    <div>
                      <Label className="text-base font-semibold mb-3 block">
                        <MapPin className="h-4 w-4 inline mr-2" />
                        {t.booking.pickup}
                      </Label>
                      <RadioGroup
                        value={formData.pickupType}
                        onValueChange={(value) => handleInputChange('pickupType', value)}
                        className="space-y-3"
                      >
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="hotel" id="pickup-hotel" />
                          <Label htmlFor="pickup-hotel">{t.booking.hotel}</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="point" id="pickup-point" />
                          <Label htmlFor="pickup-point">{t.booking.pickupPoint}</Label>
                        </div>
                      </RadioGroup>
                    </div>

                    {/* Hotel name or Pickup point */}
                    {formData.pickupType === 'hotel' ? (
                      <div>
                        <Label htmlFor="hotel" className="text-sm mb-2 block">
                          {t.booking.hotel}
                        </Label>
                        <Input
                          id="hotel"
                          value={formData.hotel}
                          onChange={(e) => handleInputChange('hotel', e.target.value)}
                          placeholder="Hotel Ritz Madrid"
                          data-testid="booking-hotel-input"
                        />
                      </div>
                    ) : (
                      <div>
                        <Label className="text-sm mb-2 block">
                          {t.booking.pickupPoint}
                        </Label>
                        <Select
                          value={formData.pickupPoint}
                          onValueChange={(value) => handleInputChange('pickupPoint', value)}
                        >
                          <SelectTrigger data-testid="pickup-point-select">
                            <SelectValue placeholder="Selecciona punto de encuentro" />
                          </SelectTrigger>
                          <SelectContent>
                            {pickupPoints.map((point) => (
                              <SelectItem key={point.value} value={point.value}>
                                {point.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}

                    {/* Special Requests */}
                    <div>
                      <Label htmlFor="requests" className="text-sm mb-2 block">
                        {t.booking.specialRequests}
                      </Label>
                      <Textarea
                        id="requests"
                        value={formData.specialRequests}
                        onChange={(e) => handleInputChange('specialRequests', e.target.value)}
                        placeholder="..."
                        rows={3}
                        data-testid="booking-requests-input"
                      />
                    </div>
                  </div>
                )}

                {/* Step 4: Payment */}
                {step === 4 && (
                  <div className="space-y-6">
                    {/* Discount Code */}
                    <div>
                      <Label className="text-base font-semibold mb-2 block">
                        Código de descuento (opcional)
                      </Label>
                      <div className="flex gap-2">
                        <Input
                          value={formData.discountCode}
                          onChange={(e) => {
                            handleInputChange('discountCode', e.target.value.toUpperCase());
                            setDiscountValidation(null);
                          }}
                          placeholder="VERANO20"
                          className="uppercase flex-1"
                          data-testid="discount-code-input"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          onClick={validateDiscount}
                          disabled={!formData.discountCode || validatingDiscount}
                          data-testid="validate-discount-btn"
                        >
                          {validatingDiscount ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            'Aplicar'
                          )}
                        </Button>
                      </div>
                      {discountValidation?.valid && (
                        <p className="text-sm text-green-600 mt-2 flex items-center gap-1">
                          <Check className="h-4 w-4" />
                          {discountValidation.message}
                        </p>
                      )}
                    </div>

                    {/* Order Summary */}
                    <div className="bg-[#FDFBF7] rounded-xl p-6">
                      <h4 className="font-semibold mb-4">Resumen de la reserva</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-[#57534E]">Tour:</span>
                          <span className="font-medium">
                            {selectedTourData?.name[language] || selectedTourData?.name.es}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-[#57534E]">Fecha:</span>
                          <span className="font-medium">
                            {formData.date?.toLocaleDateString(language === 'en' ? 'en-US' : language === 'fr' ? 'fr-FR' : 'es-ES')}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-[#57534E]">Hora:</span>
                          <span className="font-medium">{formData.time}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-[#57534E]">Personas:</span>
                          <span className="font-medium">
                            {formData.adults} adultos{formData.children > 0 ? `, ${formData.children} niños` : ''}
                          </span>
                        </div>
                        {selectedTukTuk && tukTuks.length > 0 && (
                          <div className="flex justify-between">
                            <span className="text-[#57534E]">Tuk-Tuk:</span>
                            <span className="font-medium">
                              {tukTuks.find(t => t.id === selectedTukTuk)?.name || 'Seleccionado'}
                            </span>
                          </div>
                        )}
                        <div className="border-t border-[#E7E5E4] my-4" />
                        {discountValidation?.valid && (
                          <>
                            <div className="flex justify-between">
                              <span className="text-[#57534E]">Subtotal:</span>
                              <span className="font-medium">{formatPrice(getOriginalPrice())}</span>
                            </div>
                            <div className="flex justify-between text-green-600">
                              <span>Descuento ({formData.discountCode}):</span>
                              <span>-{formatPrice(getOriginalPrice() - calculateTotal())}</span>
                            </div>
                          </>
                        )}
                        <div className="flex justify-between text-lg">
                          <span className="font-semibold">{t.booking.total}:</span>
                          <span className="font-bold text-[#C2410C]">{formatPrice(calculateTotal())}</span>
                        </div>
                      </div>
                    </div>

                    {/* Payment Method */}
                    <div>
                      <Label className="text-base font-semibold mb-3 block">
                        {t.booking.payment}
                      </Label>
                      <RadioGroup
                        value={formData.paymentMethod}
                        onValueChange={(value) => handleInputChange('paymentMethod', value)}
                        className="space-y-3"
                      >
                        <div 
                          className={`flex items-center space-x-3 p-4 rounded-lg border-2 transition-colors cursor-pointer ${
                            formData.paymentMethod === 'online' 
                              ? 'border-[#C2410C] bg-[#C2410C]/5' 
                              : 'border-[#E7E5E4] hover:border-[#C2410C]/50'
                          }`}
                          onClick={() => handleInputChange('paymentMethod', 'online')}
                        >
                          <RadioGroupItem value="online" id="pay-online" />
                          <CreditCard className="h-5 w-5 text-[#C2410C]" />
                          <Label htmlFor="pay-online" className="flex-1 cursor-pointer">
                            {t.booking.payOnline}
                          </Label>
                        </div>
                        <div 
                          className={`flex items-center space-x-3 p-4 rounded-lg border-2 transition-colors cursor-pointer ${
                            formData.paymentMethod === 'cash' 
                              ? 'border-[#C2410C] bg-[#C2410C]/5' 
                              : 'border-[#E7E5E4] hover:border-[#C2410C]/50'
                          }`}
                          onClick={() => handleInputChange('paymentMethod', 'cash')}
                        >
                          <RadioGroupItem value="cash" id="pay-cash" />
                          <Banknote className="h-5 w-5 text-[#F59E0B]" />
                          <Label htmlFor="pay-cash" className="flex-1 cursor-pointer">
                            {t.booking.payCash}
                          </Label>
                        </div>
                      </RadioGroup>
                    </div>
                  </div>
                )}
              </motion.div>
            </AnimatePresence>

            {/* Terms Checkbox - visible on step 4 */}
            {step === 4 && (
              <div className="mt-4 flex items-start gap-3">
                <input
                  type="checkbox"
                  id="accept-terms"
                  checked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e.target.checked)}
                  className="mt-1 h-4 w-4 rounded border-[#D6D3D1] text-[#C2410C] focus:ring-[#C2410C]"
                  data-testid="accept-terms-checkbox"
                />
                <label htmlFor="accept-terms" className="text-sm text-[#57534E] leading-relaxed">
                  {language === 'en' 
                    ? 'I accept the cancellation policy and service conditions'
                    : language === 'fr'
                    ? "J'accepte la politique d'annulation et les conditions de service"
                    : 'Acepto la politica de cancelacion y las condiciones del servicio'}
                </label>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-8 pt-6 border-t border-[#E7E5E4]">
              {step > 1 ? (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setStep(step - 1)}
                  className="rounded-full"
                  data-testid="booking-back-btn"
                >
                  <ChevronLeft className="h-4 w-4 mr-2" />
                  Atrás
                </Button>
              ) : (
                <div />
              )}

              {step < 4 ? (
                <Button
                  type="button"
                  onClick={() => setStep(step + 1)}
                  disabled={!canProceed()}
                  className="bg-[#C2410C] hover:bg-[#9A3412] rounded-full"
                  data-testid="booking-next-btn"
                >
                  Siguiente
                  <ChevronRight className="h-4 w-4 ml-2" />
                </Button>
              ) : (
                <Button
                  type="button"
                  onClick={handleSubmit}
                  disabled={loading || !canProceed()}
                  className="bg-[#C2410C] hover:bg-[#9A3412] rounded-full min-w-[150px]"
                  data-testid="booking-submit-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      {t.booking.processing}
                    </>
                  ) : formData.paymentMethod === 'online' ? (
                    t.booking.payNow
                  ) : (
                    t.booking.confirm
                  )}
                </Button>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default BookingModal;
